'use client';

import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountError, useBlockPart, useCommerceSource, tx, PortalLink } from '@akademi/site-builder-runtime';
import { AdapterPlaceholder, BlockRoot, sharedStyles } from '@akademi/site-builder-blocks';
import {
  AccountField,
  NEW_PASSWORD_MIN_LENGTH,
  buttonStyle,
  errorStyle,
  formStyle,
  leadStyle,
  linkRowStyle,
  mutedStyle,
  noticeStyle,
  secondaryButtonStyle,
  titleStyle,
  useAccountErrorText,
} from './accountShared';

const { sectionInner } = sharedStyles;

type Localized = string | Record<string, string>;

/**
 * Confirm a new account's email address, and choose its password.
 *
 * <p>The sign-up email carries the code twice: in the link, as `?token=`, and
 * written out for a shopper who reads it on another device. The link fills the
 * code in; either way the shopper gives the address they signed up with,
 * because a code belongs to one account and the base checks the two together.
 *
 * <p>The token is taken out of the address bar as soon as it is read. Left
 * there it would sit in the browser history, and go out in the `Referer` of
 * the first link the shopper clicks on this page.
 *
 * <p>Once the code is accepted the shopper is signed in and taken on. If that
 * sign-in does not work, the account is still ready, and the page says so and
 * offers the sign-in page instead.
 */
export interface ActivateAccountFormProps {
  title?: Localized;
  intro?: Localized;
  /** Where the shopper goes, signed in, once the account is ready. */
  successHref?: string;
  signInHref?: string;
}

export function ActivateAccountForm({
  title,
  intro,
  successHref = '/account',
  signInHref = '/sign-in',
}: ActivateAccountFormProps) {
  const commerce = useCommerceSource();
  const part = useBlockPart();
  const { t } = useTranslation('site-builder-blocks');
  const errorText = useAccountErrorText();

  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  /** Ready, but not signed in. */
  const [ready, setReady] = useState(false);
  const [codeRefused, setCodeRefused] = useState(false);
  const [resent, setResent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const url = new URL(window.location.href);
    const found = url.searchParams.get('token');
    if (found) {
      setCode(found);
      url.searchParams.delete('token');
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    }
  }, []);

  if (!commerce?.activateAccount) {
    return (
      <BlockRoot as="section">
        <div style={sectionInner}>
          <AdapterPlaceholder
            blockType="ActivateAccountForm"
            adapterName="CommerceSource"
            reason={t('commerce.account.noAdapter')}
          />
        </div>
      </BlockRoot>
    );
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!commerce?.activateAccount || busy) return;
    setBusy(true);
    setError(null);
    setCodeRefused(false);
    setResent(false);
    try {
      const profile = await commerce.activateAccount({ email: email.trim(), code: code.trim(), password });
      if (profile) {
        window.location.assign(successHref);
        return;
      }
      setReady(true);
    } catch (failure) {
      setCodeRefused(failure instanceof AccountError && failure.kind === 'INVALID_ACTIVATION');
      setError(errorText(failure));
    }
    setBusy(false);
  }

  async function resend() {
    if (!commerce?.resendActivation || busy || !email.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await commerce.resendActivation(email.trim());
      setResent(true);
      setCodeRefused(false);
    } catch (failure) {
      setError(errorText(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <BlockRoot as="section">
      <div style={{ ...sectionInner, maxWidth: 520 }}>
        <h1 {...part('title')} style={titleStyle}>
          {tx(title) || t('commerce.account.activateTitle')}
        </h1>
        {ready ? (
          <>
            <p role="status" style={noticeStyle}>
              {t('commerce.account.activateDone')}
            </p>
            <div style={linkRowStyle}>
              <PortalLink href={signInHref}>{t('commerce.account.signIn')}</PortalLink>
            </div>
          </>
        ) : (
          <>
            <p {...part('intro')} style={leadStyle}>
              {tx(intro) || t('commerce.account.activateIntro')}
            </p>
            <form onSubmit={(event) => void submit(event)} style={formStyle}>
              {error && (
                <p role="alert" style={errorStyle}>
                  {error}
                </p>
              )}
              {resent && (
                <p role="status" style={noticeStyle}>
                  {t('commerce.account.activationResent')}
                </p>
              )}
              {codeRefused && commerce.resendActivation && email.trim() && (
                <button type="button" disabled={busy} onClick={() => void resend()} style={secondaryButtonStyle}>
                  {busy ? t('commerce.account.sending') : t('commerce.account.resendActivation')}
                </button>
              )}
              <AccountField
                id="activate-email"
                label={t('commerce.account.email')}
                value={email}
                onChange={setEmail}
                type="email"
                autoComplete="email"
                required
                maxLength={254}
              />
              <AccountField
                id="activate-code"
                label={t('commerce.account.emailedCode')}
                value={code}
                onChange={setCode}
                autoComplete="one-time-code"
                required
                maxLength={64}
              />
              <AccountField
                id="activate-password"
                label={t('commerce.account.newPassword')}
                value={password}
                onChange={setPassword}
                type="password"
                autoComplete="new-password"
                required
                minLength={NEW_PASSWORD_MIN_LENGTH}
                hint={t('commerce.account.newPasswordHint', { count: NEW_PASSWORD_MIN_LENGTH })}
              />
              <button type="submit" disabled={busy} style={buttonStyle}>
                {busy ? t('commerce.account.activating') : t('commerce.account.activate')}
              </button>
            </form>
            <div style={linkRowStyle}>
              <span style={mutedStyle}>
                {t('commerce.account.haveAccount')}{' '}
                <PortalLink href={signInHref}>{t('commerce.account.signIn')}</PortalLink>
              </span>
            </div>
          </>
        )}
      </div>
    </BlockRoot>
  );
}
